import React, { useState, useEffect } from 'react';
import { Microscope, Moon, Sun, MessageSquare, Copy } from 'lucide-react';
import { ChatInterface } from './components/ChatInterface';
import { DebugConsole } from './components/DebugConsole';
import { ChatDebugConsole } from './components/ChatDebugConsole';
import { WebSocketService } from './services/websocketService';
import { useWebSocketStore } from './stores/websocketStore';
import { Family } from './types';
import { config } from './config';
import './App.css';

const API_BASE = window.FRONTEND_CONFIG?.API_BASE || config.API_BASE_URL;
const WS_BASE = window.FRONTEND_CONFIG?.WS_BASE || config.WS_BASE_URL;

const wsClient = new WebSocketService(API_BASE, WS_BASE);

const formatBuildTime = (buildTime: string) => {
  if (!buildTime || buildTime === 'Unknown') return 'Unknown';
  
  try {
    const date = new Date(buildTime);
    const now = new Date();
    
    // Check if it's today
    if (date.toDateString() === now.toDateString()) {
      return `Built @ ${date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      })} today`;
    }
    
    // Check if it's yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return `Built @ ${date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      })} yesterday`;
    }
    
    // For other dates, show formatted date
    return `Built @ ${date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    })} on ${date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: '2-digit'
    }).replace("'", "'")}`;
    
  } catch (error) {
    return `Built @ ${buildTime}`;
  }
};

function App() {
  const [families, setFamilies] = useState<Family[]>([]);
  const [selectedFamilyId, setSelectedFamilyId] = useState<string | null>(null);
  const [isDebugVisible, setIsDebugVisible] = useState<boolean>(true);
  const [isChatDebugVisible, setIsChatDebugVisible] = useState<boolean>(true);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [debugWidth, setDebugWidth] = useState<number>(() => {
    const saved = localStorage.getItem('debugWidth');
    return saved ? parseInt(saved) : 400;
  });
  const [chatDebugHeight, setChatDebugHeight] = useState<number>(() => {
    const saved = localStorage.getItem('chatDebugHeight');
    return saved ? parseInt(saved) : 300;
  });
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [isChatDebugResizing, setIsChatDebugResizing] = useState<boolean>(false);
  const { status } = useWebSocketStore();

  useEffect(() => {
    // Connect WebSocket
    wsClient.connect();
    
    // Load families
    loadFamilies();

    // Setup WebSocket event handlers
    wsClient.on('child_created', () => {
      loadFamilies();
    });

    wsClient.on('child_deleted', () => {
      loadFamilies();
    });

    wsClient.on('family_created', () => {
      loadFamilies();
    });

    return () => {
      wsClient.disconnect();
    };
  }, []);

  useEffect(() => {
    // Apply dark mode class to document root
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    // Persist to localStorage
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  useEffect(() => {
    localStorage.setItem('debugWidth', debugWidth.toString());
  }, [debugWidth]);

  useEffect(() => {
    localStorage.setItem('chatDebugHeight', chatDebugHeight.toString());
  }, [chatDebugHeight]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX;
      setDebugWidth(Math.max(300, Math.min(800, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  useEffect(() => {
    if (!isChatDebugResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newHeight = window.innerHeight - e.clientY;
      setChatDebugHeight(Math.max(200, Math.min(600, newHeight)));
    };

    const handleMouseUp = () => {
      setIsChatDebugResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isChatDebugResizing]);

  const loadFamilies = async () => {
    try {
      const response = await fetch(`${API_BASE}/export/families`);
      if (response.ok) {
        const data = await response.json();
        setFamilies(data.families || []);
      }
    } catch (error) {
      console.error('Failed to load families:', error);
    }
  };

  const handleFamilySelect = (familyId: string) => {
    setSelectedFamilyId(familyId);
    wsClient.switchFamily(familyId);
  };

  const handleCopyVersion = async () => {
    const versionText = `${window.FRONTEND_CONFIG?.APP_VERSION} (${window.FRONTEND_CONFIG?.GIT_SHA}) | ${formatBuildTime(window.FRONTEND_CONFIG?.BUILD_TIME || 'Unknown')}`;
    try {
      await navigator.clipboard.writeText(versionText);
      // Could add a toast notification here
    } catch (err) {
      console.error('Failed to copy version:', err);
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'connected': return '#238636';
      case 'connecting': return '#f7931e';
      case 'error': return '#da3633';
      default: return '#8b949e';
    }
  };

  return (
    <div className="app-container">
      <header className="app-header">
        <h1>HappyFamilyAI</h1>
        <div className="header-controls">
          <div className="connection-status">
            <span 
              className="status-indicator" 
              style={{ backgroundColor: getStatusColor() }}
            />
            <span className="status-text">
              {status === 'connected' ? 'Connected' : 
               status === 'connecting' ? 'Connecting...' : 
               status === 'error' ? 'Connection Error' : 'Disconnected'}
            </span>
          </div>
          <button 
            className="dark-mode-toggle-btn"
            onClick={() => setIsDarkMode(!isDarkMode)}
            title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <button 
            className="debug-toggle-btn"
            onClick={() => setIsDebugVisible(!isDebugVisible)}
            title={isDebugVisible ? 'Hide Debug Console' : 'Show Debug Console'}
          >
            <Microscope className="h-4 w-4" style={{ display: 'inline', marginRight: '6px' }} />
            {isDebugVisible ? 'Hide Debug' : 'Show Debug'}
          </button>
          <button 
            className="chat-debug-toggle-btn"
            onClick={() => setIsChatDebugVisible(!isChatDebugVisible)}
            title={isChatDebugVisible ? 'Hide Chat Debug' : 'Show Chat Debug'}
          >
            <MessageSquare className="h-4 w-4" style={{ display: 'inline', marginRight: '6px' }} />
            {isChatDebugVisible ? 'Hide Chat' : 'Show Chat'}
          </button>
        </div>
      </header>

      <div className="main-container">
        <main className="chat-container">
          <ChatInterface
            familyId={selectedFamilyId}
            apiBase={API_BASE}
            wsClient={wsClient}
          />
        </main>

        {isDebugVisible && (
          <>
            <div 
              className="resize-handle"
              onMouseDown={() => setIsResizing(true)}
            />
            <div className="debug-panel" style={{ width: `${debugWidth}px` }}>
              <DebugConsole
                familyId={selectedFamilyId}
                apiBase={API_BASE}
                isVisible={isDebugVisible}
                families={families}
                onFamilySelect={handleFamilySelect}
                onRefreshFamilies={loadFamilies}
                selectedFamilyId={selectedFamilyId}
                wsClient={wsClient}
              />
            </div>
          </>
        )}

        {isChatDebugVisible && (
          <>
            <div 
              className="chat-debug-resize-handle"
              onMouseDown={() => setIsChatDebugResizing(true)}
            />
            <div className="chat-debug-panel" style={{ height: `${chatDebugHeight}px` }}>
              <ChatDebugConsole />
            </div>
          </>
        )}

        <footer className="app-footer">
          <div className="version-info">
            {window.FRONTEND_CONFIG?.APP_VERSION} ({window.FRONTEND_CONFIG?.GIT_SHA}) | {formatBuildTime(window.FRONTEND_CONFIG?.BUILD_TIME || 'Unknown')}
            <button 
              className="copy-version-btn"
              onClick={handleCopyVersion}
              title="Copy version info to clipboard"
            >
              <Copy className="h-3 w-3" />
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;
